import { colors } from '@/constants/colors'
import { useEffect, useState } from 'react'
import { View } from 'react-native'
import Animated, {
	Easing,
	ReduceMotion,
	useAnimatedStyle,
	useSharedValue,
	withRepeat,
	withTiming,
} from 'react-native-reanimated'

/** How much of the width the travelling segment takes, and how long a pass lasts. */
const SEGMENT = 0.34
const PASS_MS = 1100
const THICKNESS = 2
/** The rule's own height, which the travelling bar starts below. */
const RULE = 1

/**
 * The week is being fetched, said along the rule under the wordmark.
 *
 * A spinner in a corner is a second object appearing and disappearing beside the header; this is
 * the line that is already there, working. The flow header draws a measure the same way for
 * stepped flows, so it is a gesture the app owns — the difference is that this one has no end to
 * count towards, so it travels rather than fills.
 *
 * It travels just beneath the rule, absolutely positioned so nothing below moves by two points
 * when a week loads. Not over it: the rule is ink and so is this, so laid on top it was a black
 * mark sliding along a black line and could not be seen at all. Under Reduce Motion the system
 * stops the repeat and the segment rests at its start, which is a mark rather than a movement
 * and says the same thing quietly.
 */
export const LoadingRule = ({ active }: { active: boolean }) => {
	const [width, setWidth] = useState(0)
	const travel = useSharedValue(0)

	useEffect(() => {
		if (!active) {
			return
		}
		travel.value = 0
		travel.value = withRepeat(
			withTiming(1, {
				duration: PASS_MS,
				easing: Easing.inOut(Easing.quad),
				reduceMotion: ReduceMotion.System,
			}),
			-1,
			false
		)
	}, [active, travel])

	const segment = width * SEGMENT
	const style = useAnimatedStyle(() => ({
		transform: [{ translateX: -segment + travel.value * (width + segment) }],
	}))

	// The rule's own view is one point tall and the page starts immediately under it, so a bar
	// hanging below has to be told it is in front — a later sibling paints over an earlier one's
	// overflow, and the page was covering this entirely.
	return (
		<View
			style={{ zIndex: 2 }}
			onLayout={(event) => setWidth(event.nativeEvent.layout.width)}
		>
			<View className="h-px w-full" style={{ backgroundColor: colors.ink }} />
			{active && width > 0 && (
				<View
					className="absolute left-0 right-0 overflow-hidden"
					style={{ top: RULE, height: THICKNESS, zIndex: 2 }}
					pointerEvents="none"
				>
					<Animated.View
						style={[
							{ width: segment, height: THICKNESS, backgroundColor: colors.ink },
							style,
						]}
					/>
				</View>
			)}
		</View>
	)
}
